import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';

import { api } from '../lib/api.ts';
import { data as formatarData, mesAnterior, numero } from '../lib/formato.ts';
import { baixar, csvDosAgendamentos } from '../lib/exportar.ts';
import type { Agendamento } from '../lib/tipos.ts';
import { Aviso, Botao, Campo, Carregando, Cartao, Selecao, Tabela } from '../componentes/ui.tsx';
import type { VisaoDoRelatorio } from '@engine/tipos.ts';

/** Quantas linhas a tela desenha. A exportação leva tudo o que está filtrado. */
const LIMITE = 200;

/**
 * As duas leituras do mesmo relatório.
 *
 * Não são dois relatórios: são as mesmas colunas com outro recorte de período.
 * Um agendamento marcado em 28/08 para 10/09 aparece em agosto por uma data e
 * em setembro pela outra.
 */
const VISOES: { valor: VisaoDoRelatorio; rotulo: string; explicacao: string }[] = [
  {
    valor: 'data_do_agendamento',
    rotulo: 'Data do agendamento',
    explicacao:
      'Entram os agendamentos cuja data de atendimento cai no período — é como o relatório sempre foi gerado.',
  },
  {
    valor: 'data_de_criacao',
    rotulo: 'Data de criação',
    explicacao:
      'Entram os agendamentos lançados no sistema dentro do período, mesmo que o atendimento seja para outro mês. É a visão do volume de trabalho de quem marcou.',
  },
];

export function BaseAgendamentos() {
  const padrao = mesAnterior();
  const [inicio, setInicio] = useState(padrao.inicio);
  const [fim, setFim] = useState(padrao.fim);
  // A visao escolhida so entra em vigor ao clicar em Carregar, junto com o
  // periodo: trocar o seletor nao deve refazer a consulta sozinho.
  const [visao, setVisao] = useState<VisaoDoRelatorio>('data_do_agendamento');
  const [periodo, setPeriodo] = useState<{
    inicio: string;
    fim: string;
    visao: VisaoDoRelatorio;
  } | null>(null);

  const [busca, setBusca] = useState('');
  const [usuario, setUsuario] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agendamentos', periodo],
    queryFn: () =>
      api<{ total: number; visao: VisaoDoRelatorio; agendamentos: Agendamento[] }>(
        `/fechamentos/base-agendamentos?inicio=${periodo?.inicio}&fim=${periodo?.fim}` +
          `&visao=${periodo?.visao}`,
      ),
    enabled: periodo !== null,
  });

  // As opções dos filtros vêm dos dados carregados, não de uma lista fixa: se o
  // Feegow criar um status novo, ele aparece aqui sozinho.
  const opcoes = useMemo(() => {
    const usuarios = new Set<string>();
    const status = new Set<string>();
    for (const a of data?.agendamentos ?? []) {
      if (a.usuario !== '') usuarios.add(a.usuario);
      if (a.status !== '') status.add(a.status);
    }
    return {
      usuarios: [...usuarios].sort((a, b) => a.localeCompare(b, 'pt-BR')),
      status: [...status].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    };
  }, [data]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (data?.agendamentos ?? []).filter((a) => {
      if (usuario !== '' && a.usuario !== usuario) return false;
      if (status !== '' && a.status !== status) return false;
      if (termo === '') return true;
      return [a.paciente, a.usuario, a.procedimento, a.status, a.unidade, a.prontuario]
        .filter((x): x is string => typeof x === 'string')
        .some((x) => x.toLowerCase().includes(termo));
    });
  }, [data, busca, usuario, status]);

  const filtrando = busca !== '' || usuario !== '' || status !== '';

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Base de agendamentos"
        descricao="O relatório que antes era colado à mão na planilha, agora lido direto da réplica."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Campo rotulo="Período de" tipo="date" valor={inicio} onChange={setInicio} />
          <Campo rotulo="até" tipo="date" valor={fim} onChange={setFim} />
          <Selecao
            rotulo="Recortar o período por"
            valor={visao}
            opcoes={VISOES.map((v) => v.valor)}
            permiteVazio={false}
            rotuloDaOpcao={(v) => VISOES.find((o) => o.valor === v)?.rotulo ?? v}
            onChange={(v) => setVisao(v as VisaoDoRelatorio)}
          />
          <div className="flex items-end">
            <Botao onClick={() => setPeriodo({ inicio, fim, visao })} carregando={isLoading}>
              Carregar
            </Botao>
          </div>
        </div>

        <p className="mt-3 max-w-prose text-xs text-slate-500">
          {VISOES.find((v) => v.valor === visao)?.explicacao}
        </p>
      </Cartao>

      {isError && (
        <Aviso tom="erro" titulo="Não foi possível carregar">
          <p>{error.message}</p>
        </Aviso>
      )}

      {isLoading && <Carregando texto="Lendo os agendamentos do período…" />}

      {data !== undefined && (
        <Cartao
          titulo={`${numero(data.total)} agendamentos no período · ${
            VISOES.find((v) => v.valor === data.visao)?.rotulo ?? data.visao
          }`}
          descricao={
            filtrando
              ? `${numero(filtrados.length)} depois dos filtros${filtrados.length > LIMITE ? ` — a tela mostra os primeiros ${LIMITE}` : ''}.`
              : filtrados.length > LIMITE
                ? `A tela mostra os primeiros ${LIMITE}. Use os filtros ou a busca para chegar no que procura.`
                : `Mostrando ${numero(filtrados.length)}.`
          }
          acoes={
            <Botao
              tipo="secundario"
              desabilitado={filtrados.length === 0}
              onClick={() =>
                baixar(
                  `agendamentos-${periodo?.inicio}-a-${periodo?.fim}` +
                    `-por-${periodo?.visao === 'data_de_criacao' ? 'criacao' : 'agendamento'}.csv`,
                  csvDosAgendamentos(filtrados),
                )
              }
            >
              <Download className="size-4" />
              Exportar {filtrando ? `${numero(filtrados.length)} linhas` : 'planilha'}
            </Botao>
          }
        >
          <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Selecao
              rotulo="Usuário"
              valor={usuario}
              onChange={setUsuario}
              opcoes={opcoes.usuarios}
              vazio="Todos os usuários"
            />
            <Selecao
              rotulo="Status"
              valor={status}
              onChange={setStatus}
              opcoes={opcoes.status}
              vazio="Todos os status"
            />
            <Campo rotulo="Buscar" valor={busca} onChange={setBusca} />
            {filtrando && (
              <div className="flex items-end">
                <Botao
                  tipo="secundario"
                  onClick={() => {
                    setUsuario('');
                    setStatus('');
                    setBusca('');
                  }}
                >
                  <X className="size-4" />
                  Limpar filtros
                </Botao>
              </div>
            )}
          </div>

          {filtrados.length === 0 ? (
            <p className="py-6 text-sm text-slate-500">
              Nenhum agendamento com esses filtros neste período.
            </p>
          ) : (
            <Tabela
              rolagemNoTopo
              cabecalho={
                <tr>
                  <th className="py-2 pr-3 text-right">Prontuário</th>
                  <th className="py-2 pr-3">Paciente</th>
                  <th className="py-2 pr-3">Unidade</th>
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3">Criação</th>
                  <th className="py-2 pr-3">1º atend.</th>
                  <th className="py-2 pr-3">Últ. atend.</th>
                  <th className="py-2 pr-3">Procedimento</th>
                  <th className="py-2 pr-3">Usuário</th>
                  <th className="py-2 pr-3">Status anterior</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              }
            >
              {filtrados.slice(0, LIMITE).map((a) => (
                <tr key={a.agendamentoId} className="hover:bg-slate-50">
                  <td className="num py-2 pr-3 text-slate-500">{a.prontuario ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{a.paciente ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{a.unidade ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{formatarData(a.data)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                    {formatarData(a.criacao)}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                    {formatarData(a.primeiroAtendimento)}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                    {formatarData(a.ultimoAtendimento)}
                  </td>
                  <td className="max-w-72 truncate py-2 pr-3" title={a.procedimento}>
                    {a.procedimento}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{a.usuario || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-500">
                    {a.statusAnterior ?? '—'}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{a.status}</td>
                </tr>
              ))}
            </Tabela>
          )}
        </Cartao>
      )}
    </div>
  );
}
